import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Component, Viewport, CanvasSettings } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { cn } from "@/lib/utils";
import {
  InteractionContextProvider,
  InteractionMode,
  InteractionHandlers,
  UserPermissions,
  NavigationConfig,
  SensorControlConfig,
  SensorResponse,
  CollaborationConfig,
  CollaborationResponse,
  FormSubmitConfig,
  ContentSwapConfig,
  Bounds,
} from "./InteractionContext";
import { useContentSlotActions } from "@/stores/navigation";
import type { FormSubmissionResult } from "@/types/form.types";
import { SharedCanvasCore } from "./SharedCanvasCore";
import { getViewportDimensions } from "./SharedCanvasCoreUtils";
import { ComponentWrapper } from "./ComponentWrapper";
import { PerformanceHints } from "./SharedComponentRenderer";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import {
  useViewerLayout,
  useContainerDimensions,
  calculateContentBounds,
} from "@/components/viewer/ViewerLayoutManager";
import { AddTableRowModal } from "@/components/modals/AddTableRowModal";
import { EditTableRowModal } from "@/components/modals/EditTableRowModal";
import { DeleteConfirmationDialog } from "@/components/modals/DeleteConfirmationDialog";
import { FileStorageModal } from "@/components/modals/FileStorageModal";
import { deleteTableRow } from "@/services/tableRowService";
import { AddChecklistItemModal } from "@/components/modals/AddChecklistItemModal";
import { AddGanttTaskModal } from "@/components/modals/AddGanttTaskModal";
import { EditGanttTaskModal } from "@/components/modals/EditGanttTaskModal";
import { AddKanbanItemModal } from "@/components/modals/AddKanbanItemModal";
import { EditKanbanItemModal } from "@/components/modals/EditKanbanItemModal";
import { useToast } from "@/hooks/use-toast";
import type { BaseComponentProperties } from "@/types/component-properties";
import { logger } from "@/utils/logger";
import { useOptionalTheme } from "@/design-system/themes/theme-context";

interface ViewerSpatialIndex {
  query: (bounds: { x: number; y: number; width: number; height: number }) => Component[];
}

interface SpatialIndexQuery {
  query(bounds: Bounds): Component[];
}

// Props for the viewer canvas wrapper
export interface ViewerCanvasWrapperProps {
  components: Component[];
  canvasSettings: CanvasSettings;
  viewport: Viewport;
  className?: string;
  // Future interaction handlers
  enableInteractions?: boolean;
  onNavigate?: (config: NavigationConfig) => Promise<boolean>;
  onNavigateProject?: (config: NavigationConfig) => Promise<boolean>;
  onApiCall?: (endpoint: string, data: unknown) => Promise<unknown>;
  onSensorControl?: (config: SensorControlConfig) => Promise<SensorResponse>;
  onCollaboration?: (config: CollaborationConfig) => Promise<CollaborationResponse>;
  onFormSubmit?: (config: FormSubmitConfig) => Promise<FormSubmissionResult>;
  // Performance
  spatialIndex?: SpatialIndexQuery | null; // Will be SpatialIndexingSystem when integrated
  activeColorScheme?: ColorScheme;
}

// Viewer-specific canvas wrapper
export const ViewerCanvasWrapper: React.FC<ViewerCanvasWrapperProps> = ({
  components,
  canvasSettings,
  viewport,
  className,
  enableInteractions = false,
  onNavigate,
  onNavigateProject,
  onApiCall,
  onSensorControl,
  onCollaboration,
  onFormSubmit,
  spatialIndex,
  activeColorScheme,
}) => {
  const theme = useOptionalTheme();
  const renderColorScheme = activeColorScheme ?? theme?.resolvedTheme;
  // Modal state for adding table rows
  const [isAddRowModalOpen, setIsAddRowModalOpen] = useState(false);
  const [targetTableId, setTargetTableId] = useState<string>('');

  // Modal state for adding checklist items
  const [isAddChecklistItemOpen, setIsAddChecklistItemOpen] = useState(false);
  const [targetChecklistId, setTargetChecklistId] = useState<string>('');

  // Modal state for editing table rows
  const [isEditRowModalOpen, setIsEditRowModalOpen] = useState(false);
  const [editTableId, setEditTableId] = useState<string>('');
  const [editRowId, setEditRowId] = useState<string>('');
  const [editRowData, setEditRowData] = useState<Record<string, unknown>>({});

  // Dialog state for delete confirmation
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTableId, setDeleteTableId] = useState<string>('');
  const [deleteRowId, setDeleteRowId] = useState<string>('');
  const [deleteRowData, setDeleteRowData] = useState<Record<string, unknown>>({});

  // Modal state for adding Gantt tasks
  const [isAddGanttTaskOpen, setIsAddGanttTaskOpen] = useState(false);
  const [targetGanttId, setTargetGanttId] = useState<string>('');

  // Modal state for editing Gantt tasks
  const [isEditGanttTaskOpen, setIsEditGanttTaskOpen] = useState(false);
  const [editGanttId, setEditGanttId] = useState<string>('');
  const [editGanttTaskId, setEditGanttTaskId] = useState<string>('');
  const [editGanttTaskData, setEditGanttTaskData] = useState<Record<string, unknown>>({});

  // Modal state for adding Kanban items
  const [isAddKanbanItemOpen, setIsAddKanbanItemOpen] = useState(false);
  const [targetKanbanId, setTargetKanbanId] = useState<string>('');

  // Modal state for editing Kanban items
  const [isEditKanbanItemOpen, setIsEditKanbanItemOpen] = useState(false);
  const [editKanbanId, setEditKanbanId] = useState<string>('');
  const [editKanbanItemId, setEditKanbanItemId] = useState<string>('');
  const [editKanbanItemData, setEditKanbanItemData] = useState<Record<string, unknown>>({});

  // File storage modal state
  const [isFileManagerOpen, setIsFileManagerOpen] = useState(false);
  const [fileManagerComponentId, setFileManagerComponentId] = useState<string>('');
  const [fileManagerFolder, setFileManagerFolder] = useState<string | undefined>(undefined);

  const { toast } = useToast();

  // Get viewer state
  const {
    allowInteraction,
    enableVirtualization,
    visibleBounds,
    setVisibleBounds,
    updateComponent,
    getComponentById,
  } = useViewerStore();

  // Flat Map of ALL components (including nested children) for computed metrics
  const componentMap = useViewerStore((state) => state.componentMap);

  // Container ref for measuring available width
  const containerRef = useRef<HTMLDivElement>(null);
  const containerDimensions = useContainerDimensions(containerRef);
  const responsiveContainerDimensions = useMemo(() => {
    const fallback = getViewportDimensions(viewport);
    const width = Number.isFinite(containerDimensions.width) && containerDimensions.width > 0
      ? containerDimensions.width
      : fallback.width;
    const height = Number.isFinite(containerDimensions.height) && containerDimensions.height > 0
      ? containerDimensions.height
      : fallback.height;


    return { width, height };
  }, [containerDimensions.width, containerDimensions.height, viewport]);

  // Calculate content bounds for responsive layout
  const contentBounds = useMemo(() =>
    calculateContentBounds(components),
    [components]
  );

  // ── Page-level computed metrics (cross-schema KPIs like "Percent to Goal") ──
  // Fires once after components load. Scans for multiple schemas and injects
  // deterministic computed values into KPIs that can't be served by a single query.
  const computedMetricsFiredRef = useRef(false);
  useEffect(() => {
    // Use componentMap (flat Map of ALL components including nested children)
    // instead of the `components` prop which only has root containers.
    const allComps = Array.from(componentMap.values());
    logger.debug("Viewer computed-metrics effect running", {
      componentCount: allComps.length,
      alreadyFired: computedMetricsFiredRef.current,
    });
    if (computedMetricsFiredRef.current || allComps.length === 0) return;

    // Collect schemas from all components' data sources
    const schemaMap = new Map<string, { schemaId: string; groupId: string; fields: Set<string> }>();
    for (const comp of allComps) {
      const props: BaseComponentProperties = comp.properties;
      const ds = props.dataSource;
      const qd = ds?.queryDefinition;
      if (!qd?.schemaId || !qd?.groupId) continue;
      if (!schemaMap.has(qd.schemaId)) {
        const fields = new Set<string>();
        for (const group of qd.groupBy || []) fields.add(group.field?.replace(/^data\./, '') || '');
        for (const measure of qd.measures || []) {
          fields.add(measure.field?.replace(/^data\./, '') || '');
        }
        if (qd.timeRange?.field) fields.add(String(qd.timeRange.field).replace(/^data\./, ''));
        fields.delete('');
        schemaMap.set(qd.schemaId, { schemaId: qd.schemaId, groupId: qd.groupId, fields });
      }
    }

    const schemaEntries = Array.from(schemaMap.values());
    if (schemaEntries.length < 2) return; // Need at least 2 schemas for cross-schema metrics

    const goalsSchema = schemaEntries.find(e =>
      e.fields.has('target_weight') || e.fields.has('goal_weight') || e.fields.has('goalWeight')
    );
    const entriesSchema = schemaEntries.find(e =>
      e !== goalsSchema && (e.fields.has('weight') || e.fields.has('date'))
    );
    if (!entriesSchema || !goalsSchema) return;

    computedMetricsFiredRef.current = true;
    const groupId = entriesSchema.groupId;
    logger.debug("Viewer firing page-level computed-metrics", {
      groupId,
      entries: entriesSchema.schemaId,
      goals: goalsSchema.schemaId,
    });

    import('@/services/api/dataApi').then(({ default: dataApi }) =>
      dataApi.computeMetrics({
        groupId,
        metrics: ['percentToGoal', 'daysToGoal', 'currentValue', 'goalValue', 'changeFromStart', 'remainingToGoal'],
        schemas: { entries: entriesSchema.schemaId, goals: goalsSchema.schemaId },
      })
    ).then(result => {
      logger.debug("Viewer computed metrics result", result);

      // Only inject cross-schema metrics that no single query template can serve.
      // KPIs like Current Weight, Days to Goal, Goal Weight have their own query
      // templates. Percent to Goal requires cross-schema computation.
      const crossSchemaLabels: Record<string, string> = {
        'percent to goal': 'percentToGoal',
        'progress': 'percentToGoal',
      };

      // Build parent → children index
      const parentChildren = new Map<string, typeof allComps>();
      for (const comp of allComps) {
        if (!comp.parentId) continue;
        const siblings = parentChildren.get(comp.parentId) || [];
        siblings.push(comp);
        parentChildren.set(comp.parentId, siblings);
      }

      const containerTypes = new Set(['flex_container', 'container', 'form_container']);

      const findValueComponent = (labelComp: typeof allComps[0]) => {
        const siblings = parentChildren.get(labelComp.parentId || '') || [];
        const direct = siblings.find(s => s.id !== labelComp.id && !containerTypes.has(s.type));
        if (direct) return direct;
        for (const sib of siblings) {
          if (sib.id === labelComp.id) continue;
          const grandchildren = parentChildren.get(sib.id) || [];
          const nested = grandchildren.find(gc => !containerTypes.has(gc.type));
          if (nested) return nested;
        }
        return null;
      };

      for (const comp of allComps) {
        const props: BaseComponentProperties = comp.properties;
        const content = String(props.content ?? '').toLowerCase();
        if (!content) continue;
        for (const [keyword, metricKey] of Object.entries(crossSchemaLabels)) {
          if (content.includes(keyword) && result[metricKey] != null) {
            const valueComp = findValueComponent(comp);
            if (valueComp) {
              const metricValue = result[metricKey];
              // Inject pre-formatted value and clear formatter/suffix/unit to prevent
              // double-formatting (e.g., formatter adds "%" AND suffix adds " %").
              const vProps = valueComp.properties;
              const formatted = `${metricValue}%`;
              updateComponent(valueComp.id, {
                properties: {
                  ...vProps,
                  value: formatted,
                  valueFormatter: undefined,
                  suffix: undefined,
                  unit: undefined,
                },
              });
              import('@/stores/editor/component-data-store').then(({ useComponentDataStore }) => {
                useComponentDataStore.getState().setData(valueComp.id, [{ value: formatted }]);
              });
              logger.info("Injected cross-schema computed metric into viewer component", {
                metricKey,
                value: formatted,
                componentId: valueComp.id,
              });
            }
            break;
          }
        }
      }
    }).catch(err => {
      logger.warn("Viewer computed metrics failed; continuing without injection", err);
    });
  }, [componentMap, updateComponent]);

  // Use viewer-specific layout logic
  const { canvasStyles } = useViewerLayout({
    containerWidth: containerDimensions.width,
    contentBounds,
    viewport,
    components,
  });

  // Determine interaction mode
  const interactionMode: InteractionMode = useMemo(() => {
    if (enableInteractions && allowInteraction) {
      return "functional";
    }
    return "viewing";
  }, [enableInteractions, allowInteraction]);

  // User permissions for viewer mode
  const userPermissions: UserPermissions = useMemo(() => ({
    canEdit: false,
    canView: true,
    canInteract: enableInteractions && allowInteraction,
    isOwner: false,
  }), [enableInteractions, allowInteraction]);

  // Toggle visibility handler for Load More buttons and Tab switching
  const handleToggleVisibility = useCallback((targetIds: string[], showOnlyThisId?: string) => {
    logger.debug("Viewer toggling component visibility", { targetIds, showOnlyThisId });

    if (showOnlyThisId) {
      // Check if this is accordion mode (collapsible) or tab mode (always one visible)
      const isAccordionMode = showOnlyThisId.startsWith('accordion-content-');

      // Check if the target item is already visible (for collapsible accordion)
      const targetComponent = getComponentById(showOnlyThisId);
      const isAlreadyVisible = targetComponent && !targetComponent.hidden;

      if (isAccordionMode && isAlreadyVisible) {
        // Collapsible accordion: hide the already-visible item
        logger.debug("Viewer hiding open accordion item", { componentId: showOnlyThisId });
        updateComponent(showOnlyThisId, { hidden: true });

        // Update the corresponding button icon to ChevronDown (closed state)
        const contentMatch = showOnlyThisId.match(/accordion-content-(\d+)$/);
        if (contentMatch) {
          const itemNumber = contentMatch[1];
          const buttonId = `accordion-header-button-${itemNumber}`;
          const button = getComponentById(buttonId);
          if (button) {
            const newProperties = {
              ...button.properties,
              iconName: "ChevronDown",
            };
            updateComponent(buttonId, { properties: newProperties });
          }
        }
        return; // Exit early
      }

      // Tab switching mode OR Accordion showing a new item: show only the specified item, hide all others
      logger.debug("Viewer showing a single content target", { componentId: showOnlyThisId });

      // Update tab content visibility
      targetIds.forEach(id => {
        const component = getComponentById(id);
        if (component) {
          const shouldShow = id === showOnlyThisId;
          updateComponent(id, { hidden: !shouldShow });
        } else {
          logger.warn("Viewer visibility target was not found", { componentId: id });
        }
      });

      // Update accordion button icons if in accordion mode
      if (isAccordionMode) {
        targetIds.forEach(contentId => {
          const contentMatch = contentId.match(/accordion-content-(\d+)$/);
          if (contentMatch) {
            const itemNumber = contentMatch[1];
            const buttonId = `accordion-header-button-${itemNumber}`;
            const button = getComponentById(buttonId);

            if (button) {
              const isOpen = contentId === showOnlyThisId;
              const newProperties = {
                ...button.properties,
                iconName: isOpen ? "ChevronUp" : "ChevronDown",
              };
              updateComponent(buttonId, { properties: newProperties });
            }
          }
        });
      }

      // Update tab button styles to highlight the active tab
      // Extract tab name from showOnlyThisId (e.g., "overview" from "tab-content-overview")
      const tabMatch = showOnlyThisId.match(/tab-content-(.+)$/);
      if (tabMatch) {
        const activeTabName = tabMatch[1];
        const activeButtonId = `tab-button-${activeTabName}`;
        logger.debug("Viewer updating active tab button styles", { activeButtonId });

        // Update all tab buttons to set active/inactive styling
        targetIds.forEach(contentId => {
          const contentMatch = contentId.match(/tab-content-(.+)$/);
          if (contentMatch) {
            const tabName = contentMatch[1];
            const buttonId = `tab-button-${tabName}`;
            const button = getComponentById(buttonId);

            if (button) {
              const isActive = buttonId === activeButtonId;
              const newProperties = {
                ...button.properties,
                color: isActive ? "#3B82F6" : "#6B7280",
                fontWeight: isActive ? "600" : "500",
                borderBottom: isActive ? "2px solid #3B82F6" : "none",
              };

              updateComponent(buttonId, { properties: newProperties });
            }
          }
        });
      }
    } else {
      // Original toggle mode: toggle each component
      targetIds.forEach(id => {
        const component = getComponentById(id);
        if (component) {
          updateComponent(id, { hidden: !component.hidden });
        } else {
          logger.warn("Viewer toggle target was not found", { componentId: id });
        }
      });
    }
  }, [getComponentById, updateComponent]);

  // Content slot actions for swap-content
  const { setActiveContent } = useContentSlotActions();

  // Swap content handler (Phase 4)
  const handleSwapContent = useCallback((config: ContentSwapConfig) => {
    const { containerId, targetContentId, transition } = config;
    logger.debug("Viewer swapping content", { containerId, targetContentId, transition });
    setActiveContent(containerId, targetContentId);
  }, [setActiveContent]);

  // Handler for opening add table row modal
  const handleAddTableRow = useCallback((tableId: string) => {
    logger.debug("Viewer opening add table row modal", { tableId });
    setTargetTableId(tableId);
    setIsAddRowModalOpen(true);
  }, []);

  // Handler for closing the add modal
  const handleCloseModal = useCallback(() => {
    setIsAddRowModalOpen(false);
    setTargetTableId('');
  }, []);

  // Handler for opening add checklist item modal
  const handleAddChecklistItem = useCallback((checklistId: string) => {
    setTargetChecklistId(checklistId);
    setIsAddChecklistItemOpen(true);
  }, []);

  // Handler for closing the checklist item modal
  const handleCloseChecklistModal = useCallback(() => {
    setIsAddChecklistItemOpen(false);
    setTargetChecklistId('');
  }, []);

  // Handler for opening add Gantt task modal
  const handleAddGanttTask = useCallback((ganttId: string) => {
    setTargetGanttId(ganttId);
    setIsAddGanttTaskOpen(true);
  }, []);

  // Handler for closing the Gantt task modal
  const handleCloseGanttTaskModal = useCallback(() => {
    setIsAddGanttTaskOpen(false);
    setTargetGanttId('');
  }, []);

  // Handler for opening edit Gantt task modal
  const handleEditGanttTask = useCallback((ganttId: string, taskId: string, taskData: Record<string, unknown>) => {
    setEditGanttId(ganttId);
    setEditGanttTaskId(taskId);
    setEditGanttTaskData(taskData);
    setIsEditGanttTaskOpen(true);
  }, []);

  // Handler for closing the edit Gantt task modal
  const handleCloseEditGanttTaskModal = useCallback(() => {
    setIsEditGanttTaskOpen(false);
    setEditGanttId('');
    setEditGanttTaskId('');
    setEditGanttTaskData({});
  }, []);

  // Handler for opening add Kanban item modal
  const handleAddKanbanItem = useCallback((kanbanId: string) => {
    setTargetKanbanId(kanbanId);
    setIsAddKanbanItemOpen(true);
  }, []);

  // Handler for closing the Kanban item modal
  const handleCloseKanbanItemModal = useCallback(() => {
    setIsAddKanbanItemOpen(false);
    setTargetKanbanId('');
  }, []);

  // Handler for opening edit Kanban item modal
  const handleEditKanbanItem = useCallback((kanbanId: string, itemId: string, itemData: Record<string, unknown>) => {
    setEditKanbanId(kanbanId);
    setEditKanbanItemId(itemId);
    setEditKanbanItemData(itemData);
    setIsEditKanbanItemOpen(true);
  }, []);

  // Handler for closing the edit Kanban item modal
  const handleCloseEditKanbanItemModal = useCallback(() => {
    setIsEditKanbanItemOpen(false);
    setEditKanbanId('');
    setEditKanbanItemId('');
    setEditKanbanItemData({});
  }, []);

  // Handler for opening edit table row modal
  const handleEditTableRow = useCallback((tableId: string, rowId: string, rowData: Record<string, unknown>) => {
    logger.debug("Viewer opening edit table row modal", { tableId, rowId });
    setEditTableId(tableId);
    setEditRowId(rowId);
    setEditRowData(rowData);
    setIsEditRowModalOpen(true);
  }, []);

  // Handler for closing edit modal
  const handleCloseEditModal = useCallback(() => {
    setIsEditRowModalOpen(false);
    setEditTableId('');
    setEditRowId('');
    setEditRowData({});
  }, []);

  // Handler for opening delete confirmation dialog
  const handleDeleteTableRow = useCallback((tableId: string, rowId: string, rowData: Record<string, unknown>) => {
    logger.debug("Viewer opening delete table row confirmation", { tableId, rowId });
    setDeleteTableId(tableId);
    setDeleteRowId(rowId);
    setDeleteRowData(rowData);
    setIsDeleteDialogOpen(true);
  }, []);

  // Handler for closing delete dialog
  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDeleteTableId('');
    setDeleteRowId('');
    setDeleteRowData({});
  }, []);

  // Handler for confirming delete
  const handleConfirmDelete = useCallback(() => {
    const success = deleteTableRow(deleteTableId, deleteRowId);

    if (success) {
      handleCloseDeleteDialog();

      // Show toast after closing to avoid React warning
      setTimeout(() => {
        toast({
          title: 'Success',
          description: 'Row deleted successfully',
        });
      }, 100);
    } else {
      toast({
        title: 'Error',
        description: 'Failed to delete row',
        variant: 'destructive',
      });
    }
  }, [deleteTableId, deleteRowId, toast, handleCloseDeleteDialog]);

  // File storage modal handlers
  const handleOpenFileManager = useCallback((componentId: string, folder?: string) => {
    logger.debug("Viewer opening file manager modal", { componentId, folder });
    setFileManagerComponentId(componentId);
    setFileManagerFolder(folder);
    setIsFileManagerOpen(true);
  }, []);

  const handleCloseFileManager = useCallback(() => {
    logger.debug("Viewer closing file manager modal");
    setIsFileManagerOpen(false);
    setFileManagerComponentId('');
    setFileManagerFolder(undefined);
  }, []);

  // Default form submit handler using form registry
  const defaultFormSubmitHandler = useCallback(async (config: FormSubmitConfig): Promise<FormSubmissionResult> => {
    const { formContainerId } = config;
    logger.debug("Viewer default form submit handler triggered", { formContainerId });

    try {
      const { formRegistry } = await import("@/services/form/FormSubmissionService");
      const result = await formRegistry.submit(formContainerId);
      logger.debug("Viewer form submission completed", {
        formContainerId,
        success: result.success,
        errorCount: result.errors?.length ?? 0,
      });
      return result;
    } catch (error) {
      logger.error("Viewer form submission error", error);
      return {
        success: false,
        errors: [{
          code: "SUBMISSION_ERROR",
          message: error instanceof Error ? error.message : "Form submission failed",
        }],
      };
    }
  }, []);

  // Interaction handlers for functional mode
  const interactionHandlers: InteractionHandlers = useMemo(() => {
    if (interactionMode === "viewing") {
      return {};
    }

    return {
      onNavigate,
      onNavigateProject,
      onApiCall,
      onSensorControl,
      onCollaboration,
      onFormSubmit: onFormSubmit || defaultFormSubmitHandler,
      onToggleVisibility: handleToggleVisibility,
      onSwapContent: handleSwapContent,
      onAddTableRow: handleAddTableRow,
      onEditTableRow: handleEditTableRow,
      onDeleteTableRow: handleDeleteTableRow,
      onOpenFileManager: handleOpenFileManager,
      onAddChecklistItem: handleAddChecklistItem,
      onAddGanttTask: handleAddGanttTask,
      onEditGanttTask: handleEditGanttTask,
      onAddKanbanItem: handleAddKanbanItem,
      onEditKanbanItem: handleEditKanbanItem,
    };
  }, [
    interactionMode,
    onNavigate,
    onNavigateProject,
    onApiCall,
    onSensorControl,
    onCollaboration,
    onFormSubmit,
    defaultFormSubmitHandler,
    handleToggleVisibility,
    handleSwapContent,
    handleAddTableRow,
    handleEditTableRow,
    handleDeleteTableRow,
    handleOpenFileManager,
    handleAddChecklistItem,
    handleAddGanttTask,
    handleEditGanttTask,
    handleAddKanbanItem,
    handleEditKanbanItem,
  ]);

  // Handle viewport scroll for virtualization
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!enableVirtualization) return;

      const target = e.currentTarget;
      const bounds = {
        x: target.scrollLeft,
        y: target.scrollTop,
        width: target.clientWidth,
        height: target.clientHeight,
      };

      setVisibleBounds(bounds);
    },
    [enableVirtualization, setVisibleBounds]
  );

  // Performance hints for rendering
  const performanceHints: PerformanceHints = useMemo(() => ({
    enableVirtualization,
    spatialIndex,
    viewportBounds: visibleBounds || {
      x: 0,
      y: 0,
      ...getViewportDimensions(viewport),
    },
  }), [enableVirtualization, spatialIndex, visibleBounds, viewport]);

  // Filter visible components for performance
  const visibleComponents = useMemo(() => {
    if (!enableVirtualization || !visibleBounds) {
      return components;
    }

    // Use spatial index if available
    if (spatialIndex) {
      return spatialIndex.query(visibleBounds);
    }

    // Manual filtering fallback
    return components.filter((component) => {
      // Skip components without position or size (e.g., children in flex containers)
      if (!component.position || !component.size) {
        return true; // Include components without bounds (they'll be handled by flex layout)
      }

      const width = typeof component.size.width === 'number' ? component.size.width : 0;
      const height = typeof component.size.height === 'number' ? component.size.height : 0;

      // Include components with non-numeric dimensions (they use relative sizing)
      if (width === 0 || height === 0) {
        return true;
      }

      const compLeft = component.position.x ?? 0;
      const compTop = component.position.y ?? 0;
      const compRight = compLeft + width;
      const compBottom = compTop + height;

      return !(
        compRight < visibleBounds.x ||
        compLeft > visibleBounds.x + visibleBounds.width ||
        compBottom < visibleBounds.y ||
        compTop > visibleBounds.y + visibleBounds.height
      );
    });
  }, [components, enableVirtualization, visibleBounds, spatialIndex]);

  const deleteRowName =
    typeof deleteRowData.name === "string" && deleteRowData.name.trim().length > 0
      ? deleteRowData.name
      : "this row";

  return (
    <InteractionContextProvider
      mode={interactionMode}
      userPermissions={userPermissions}
      handlers={interactionHandlers}
    >
      <div
        ref={containerRef}
        className={cn("viewer-canvas-wrapper", "w-full", className)}
        style={canvasStyles.wrapper}
      >
        <div
          onScroll={handleScroll}
          style={{
            ...canvasStyles.container,
            width: '100%', // Always use full width
            position: 'relative',
          }}
        >
          <SharedCanvasCore
            canvasSettings={canvasSettings}
            viewport={viewport}
            components={components} // Use all components, not just visible ones for consistent positioning
            zoom={1} // Viewer always at 1.0 zoom for clean viewing
            spatialIndex={spatialIndex}
            className="viewer-canvas w-full"
            layoutMode="viewer" // Explicit viewer mode
          >
          {/* Render components in viewer mode - only render root level components (those without parentId) */}
          {visibleComponents
            .filter((component: Component) => !component.parentId) // Only render components without parents
            .map((component: Component) => (
            <ComponentWrapper
              key={component.id}
              component={component}
              className="viewer-component"
              viewport={viewport}
              activeColorScheme={renderColorScheme}
              containerDimensions={responsiveContainerDimensions}
              enableVirtualization={performanceHints.enableVirtualization}
              viewportBounds={performanceHints.viewportBounds}
              spatialIndex={spatialIndex}
            />
          ))}
        </SharedCanvasCore>
        </div>
      </div>

      {/* Add Table Row Modal */}
      <AddTableRowModal
        isOpen={isAddRowModalOpen}
        onClose={handleCloseModal}
        tableComponentId={targetTableId}
      />

      {/* Add Checklist Item Modal */}
      <AddChecklistItemModal
        isOpen={isAddChecklistItemOpen}
        onClose={handleCloseChecklistModal}
        checklistComponentId={targetChecklistId}
      />

      {/* Edit Table Row Modal */}
      <EditTableRowModal
        isOpen={isEditRowModalOpen}
        onClose={handleCloseEditModal}
        tableComponentId={editTableId}
        rowId={editRowId}
        rowData={editRowData}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        itemName={deleteRowName}
      />

      {/* File Storage Modal */}
      <FileStorageModal
        isOpen={isFileManagerOpen}
        onClose={handleCloseFileManager}
        componentId={fileManagerComponentId}
        currentFolder={fileManagerFolder}
      />

      {/* Add Gantt Task Modal */}
      <AddGanttTaskModal
        isOpen={isAddGanttTaskOpen}
        onClose={handleCloseGanttTaskModal}
        ganttComponentId={targetGanttId}
      />

      {/* Edit Gantt Task Modal */}
      <EditGanttTaskModal
        isOpen={isEditGanttTaskOpen}
        onClose={handleCloseEditGanttTaskModal}
        ganttComponentId={editGanttId}
        taskId={editGanttTaskId}
        taskData={editGanttTaskData}
      />

      {/* Add Kanban Item Modal */}
      <AddKanbanItemModal
        isOpen={isAddKanbanItemOpen}
        onClose={handleCloseKanbanItemModal}
        kanbanComponentId={targetKanbanId}
      />

      {/* Edit Kanban Item Modal */}
      <EditKanbanItemModal
        isOpen={isEditKanbanItemOpen}
        onClose={handleCloseEditKanbanItemModal}
        kanbanComponentId={editKanbanId}
        itemId={editKanbanItemId}
        itemData={editKanbanItemData}
      />
    </InteractionContextProvider>
  );
};

// Simple footer component for viewer
export const ViewerFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-background border-t border-border mt-6">
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-muted-foreground">
            <span>© {currentYear} TerraMatrix. All rights reserved.</span>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>Version 1.0.0</span>
            <span>•</span>
            <div className="inline-flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              All systems operational
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ViewerCanvasWrapper;
